/**
 * Boot sequence — fires once per day per device when the owner first opens the
 * console. Plays an 8-second Web-Audio-synth "Iron-Man-HUD" stinger (no
 * external file required, works offline) and then triggers a time-of-day
 * greeting via Jarvis. If the synth fails (autoplay blocked, no audio context)
 * we skip silently and just play the greeting on the next user gesture.
 *
 * Returns a promise that resolves when the stinger finishes (or immediately
 * if skipped).
 */

const BOOT_KEY = "rawco_boot_fired_this_session"; // sessionStorage-scoped — clears on tab close
const LATE_NIGHT_HOURS = new Set([23, 0, 1, 2, 3, 4]); // 11pm–5am

export function shouldFireBoot() {
  // Owner explicitly asked Feb 2026: "the morning brief should be
  // automatically displayed every time the app is opened." Previously
  // this was localStorage-scoped (once per day per device) which meant
  // re-opening the app later in the day silently skipped the brief.
  // Switched to sessionStorage so the boot sequence + auto-brief fires
  // on every fresh tab / page reload, but still de-dupes within the
  // same tab so rapid React re-mounts don't double-fire.
  try {
    return sessionStorage.getItem(BOOT_KEY) !== "1";
  } catch {
    return true;
  }
}

export function markBootFired() {
  try { sessionStorage.setItem(BOOT_KEY, "1"); } catch {}
}

export function greetingForHour(hour = new Date().getHours()) {
  // Owner asked: "cut out how many times it says 'good morning' — change
  // it to mate, pal, geezer, dude, bro, fella, swop them up a bit." We
  // pick a random mate-pool nickname each boot so it never feels stale.
  const matePool = ["mate", "pal", "geezer", "dude", "bro", "fella", "lad"];
  const m = matePool[Math.floor(Math.random() * matePool.length)];
  if (LATE_NIGHT_HOURS.has(hour)) {
    return [
      `Up late, ${m}? I'm online and listening.`,
      `Burning the midnight oil, ${m}? I'm right here when you need me.`,
      `Late one tonight, ${m}? All systems are up.`,
    ][Math.floor(Math.random() * 3)];
  }
  if (hour < 12) {
    return [
      `Morning, ${m}. All systems are up. What's the play?`,
      `Top of the morning, ${m}. Ready when you are.`,
      `Right ${m}, fresh day. Let's crack on.`,
      `Up and at 'em, ${m}. What's first?`,
    ][Math.floor(Math.random() * 4)];
  }
  if (hour < 18) {
    return [
      `Afternoon, ${m}. Ready when you are.`,
      `Alright ${m}, what we doing?`,
      `Back at it, ${m}. Standing by.`,
    ][Math.floor(Math.random() * 3)];
  }
  return [
    `Evening, ${m}. Standing by.`,
    `Alright ${m}, what's on?`,
    `Good of you to drop in, ${m}. Ready when you are.`,
  ][Math.floor(Math.random() * 3)];
}

/**
 * Play the owner's uploaded wake song if one exists. Returns true on success.
 * Falls back silently otherwise — caller can chain `playBootStinger()`.
 */
export async function playWakeSongIfUploaded() {
  try {
    // Cheap status probe first — returns 200 JSON {exists: bool} so the
    // browser doesn't log a 404 in the console when no song is uploaded
    // (which was the only pre-existing console noise after the audio fix).
    const statusUrl = `${process.env.REACT_APP_BACKEND_URL}/api/jarvis/wake_song_status`;
    const status = await fetch(statusUrl).then(r => r.json()).catch(() => ({ exists: false }));
    if (!status?.exists) return false;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/jarvis/wake_song`;
    const probe = await fetch(url, { method: "GET" });
    if (!probe.ok) return false;
    const blob = await probe.blob();
    const objectUrl = URL.createObjectURL(blob);
    return await new Promise((resolve) => {
      const a = new Audio(objectUrl);
      a.volume = 0.85;
      a.onended = () => { URL.revokeObjectURL(objectUrl); resolve(true); };
      a.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(false); };
      // Auto-cut at 10s — owner asked for "10 seconds of the Clash"
      setTimeout(() => { try { a.pause(); a.currentTime = 0; } catch {}; URL.revokeObjectURL(objectUrl); resolve(true); }, 10500);
      a.play().catch(() => { URL.revokeObjectURL(objectUrl); resolve(false); });
    });
  } catch (e) {
    console.warn("[boot] wake song fetch failed:", e?.message);
    return false;
  }
}

/**
 * Web-Audio synthesised "HUD power-on" stinger ~8 seconds.
 * Layers: low rising drone + sweep pad + tick metronome + final swell.
 * No external file required — pure Web Audio.
 */
export async function playBootStinger() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    const ctx = new AC();
    if (ctx.state === "suspended") await ctx.resume();
    const now = ctx.currentTime;
    const dur = 8;

    // Master bus with gentle reverb-ish low-pass and a gain envelope
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.55, now + 0.6);
    master.gain.exponentialRampToValueAtTime(0.85, now + 6.5);
    master.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(800, now);
    lp.frequency.exponentialRampToValueAtTime(6000, now + dur * 0.75);
    master.connect(lp);
    lp.connect(ctx.destination);

    // Layer 1 — sub drone rising from C2 → C3
    const drone = ctx.createOscillator();
    drone.type = "sawtooth";
    drone.frequency.setValueAtTime(65, now);
    drone.frequency.exponentialRampToValueAtTime(130, now + dur);
    const droneGain = ctx.createGain();
    droneGain.gain.value = 0.18;
    drone.connect(droneGain);
    droneGain.connect(master);
    drone.start(now); drone.stop(now + dur);

    // Layer 2 — golden pad (triangle stack)
    const padFreqs = [220, 277, 330, 440];
    padFreqs.forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = "triangle";
      o.frequency.value = f;
      o.detune.value = (i - 1.5) * 4;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.06, now + 1.5 + i * 0.4);
      g.gain.exponentialRampToValueAtTime(0.0001, now + dur - 0.2);
      o.connect(g); g.connect(master);
      o.start(now + 0.6 + i * 0.15); o.stop(now + dur);
    });

    // Layer 3 — staccato HUD ticks (every 0.6s for first 4s)
    for (let i = 0; i < 6; i++) {
      const t = now + 0.8 + i * 0.6;
      const o = ctx.createOscillator();
      o.type = "square";
      o.frequency.value = 1760 + i * 60;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.04, t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
      o.connect(g); g.connect(master);
      o.start(t); o.stop(t + 0.1);
    }

    // Layer 4 — final swell stinger at t=5.5s
    const swell = ctx.createOscillator();
    swell.type = "sine";
    swell.frequency.setValueAtTime(330, now + 5.5);
    swell.frequency.exponentialRampToValueAtTime(880, now + 7.6);
    const swellG = ctx.createGain();
    swellG.gain.setValueAtTime(0.0001, now + 5.5);
    swellG.gain.exponentialRampToValueAtTime(0.18, now + 6.8);
    swellG.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    swell.connect(swellG); swellG.connect(master);
    swell.start(now + 5.5); swell.stop(now + dur);

    // Resolve when the stinger finishes
    await new Promise((res) => setTimeout(res, dur * 1000));
    try { ctx.close(); } catch {}
    return true;
  } catch (e) {
    console.warn("[boot] stinger failed:", e?.message);
    return false;
  }
}
